import * as R from 'ramda';
import { throttle } from 'throttle-debounce';

import { getStructureAndFloorAtPoint } from '@utils/geom.ts';

import type {
  BBox,
  DeepLinkViewState,
  Id,
  MapFeature,
  MapInitialized,
  MapInstance,
  MapRendererApp,
  VenueStructure,
  ViewState,
} from './types.ts';
import { convertRadiusToZoom, convertZoomToRadius, getMapState } from './utils.ts';

type MapRendererConfig = {
  defaultViewParams?: Partial<ViewState>;
  deepLinkProps?: DeepLinkViewState;
};

type DefaultView = Pick<ViewState, 'lat' | 'lng' | 'zoom'> & Partial<Pick<ViewState, 'bearing' | 'pitch'>>;

type StateControllerState = {
  structures?: VenueStructure[];
  defaultView?: DefaultView;
};

type MoveEndPayload = ViewState & { mapAction: string };
type CenterPosition = {
  lat: number;
  lng: number;
  ordinal: number | null;
  structureId?: Id | null;
  floorId?: Id | null;
};

export default function StateController(
  app: MapRendererApp,
  mapInitialized: MapInitialized,
  config: MapRendererConfig,
) {
  let state: StateControllerState = {};

  mapInitialized().then((map: MapInstance) => {
    map.on('move', (e: { originalEvent?: unknown }) => {
      if (e.originalEvent) {
        dispatchUserMoving(map);
      }
    });

    let userPanStarted = false;
    let userZoomStarted = false;
    let startZoom: number | null = null;

    map.on('dragstart', (e: { originalEvent?: unknown }) => {
      if (e.originalEvent) {
        userPanStarted = true;
      }
    });

    map.on('zoomstart', (e: { originalEvent?: unknown }) => {
      if (e.originalEvent) {
        userZoomStarted = true;
        if (startZoom === null) {
          startZoom = map.getZoom();
        }
      }
    });

    map.on('movestart', (e: { originalEvent?: unknown }) => {
      if (e.originalEvent) {
        app.bus.send('map/userMoveStart', getMapState(map));
      }
    });

    map.on('moveend', e => {
      const mapState = getMapState(map);
      const eventAction = (e as { mapAction?: string }).mapAction;

      let mapAction = 'other';
      if (userPanStarted && userZoomStarted && startZoom !== null) {
        mapAction = mapState.zoom > startZoom ? 'panAndZoomIn' : 'panAndZoomOut';
      } else if (userPanStarted) {
        mapAction = 'pan';
      } else if (userZoomStarted && startZoom !== null) {
        mapAction = mapState.zoom > startZoom ? 'zoomIn' : 'zoomOut';
      } else if (eventAction) {
        mapAction = eventAction;
      }

      userPanStarted = false;
      userZoomStarted = false;
      startZoom = null;

      const payload = { ...mapState, mapAction };
      app.bus.send('map/moveEnd', payload);
      notifyState(payload);
    });
  });

  const dispatchUserMoving = throttle(200, (map: MapInstance) => app.bus.send('map/userMoving', getMapState(map)), {
    noLeading: true,
  });

  app.bus.on(
    'venueData/mapDataLoaded',
    ({
      structures,
      venueCenter,
      venueRadius,
    }: {
      structures: VenueStructure[];
      venueCenter: [number, number];
      venueRadius: number;
    }) => {
      state = { ...state, structures };
      mapInitialized().then((map: MapInstance) => {
        const [lat, lng] = venueCenter;
        const zoom = convertRadiusToZoom(venueRadius, { lat, lng }, map, app.config.renderDiv);
        const defaultView = state.defaultView || { lat, lng, zoom, ...config.defaultViewParams };
        state = { ...state, defaultView };
        app.bus.send('map/animateToView', {
          ...defaultView,
          center: [defaultView.lng, defaultView.lat],
          relSpeed: Infinity,
        });
        if (config.deepLinkProps) {
          animateToState(config.deepLinkProps);
        }
      });
    },
  );

  const animateToState = (viewParams: DeepLinkViewState): void => {
    if (viewParams.vp) {
      const { bearing, lat, lng, pitch, zoom } = viewParams.vp;
      app.bus.send('map/animateToView', {
        bearing,
        zoom,
        pitch,
        center: lng !== undefined && lat !== undefined ? [lng, lat] : undefined,
        relSpeed: Infinity,
      });
    }
    const { bearing, lat, lng, pitch, zoom, radius, buildingId, floorId } = viewParams;
    if (floorId) {
      app.bus.send('map/selectFloor', { id: floorId });
    } else if (buildingId) {
      app.bus.send('mapLevelSelector/selectBuilding', { id: buildingId });
    }
    if ([bearing, lat, pitch, zoom, radius].some(value => value !== undefined && value !== null)) {
      const view: Partial<ViewState> & {
        radius?: number;
        center?: [number, number];
      } = {};
      if (bearing !== undefined && bearing !== null) {
        view.bearing = Number.parseFloat(String(bearing));
      }
      if (pitch !== undefined && pitch !== null) {
        view.pitch = Number.parseFloat(String(pitch));
      }
      if (zoom !== undefined && zoom !== null) {
        view.zoom = Number.parseFloat(String(zoom));
      }
      if (radius !== undefined && radius !== null) {
        view.radius = Number.parseFloat(String(radius));
      }
      if (lat !== undefined && lat !== null) {
        view.lat = Number.parseFloat(String(lat));
      }
      if (lng !== undefined && lng !== null) {
        view.lng = Number.parseFloat(String(lng));
      }

      if (radius) {
        app.bus.send('map/animateToPointWithRadius', {
          ...view,
          relSpeed: 5,
          // Ramda's Pick return type lacks the implicit index signature that
          // BusPayload assignability needs; restate it as a plain object type.
          animOptions: R.pick(['pitch', 'bearing'], view) as {
            pitch?: number;
            bearing?: number;
          },
        });
      } else {
        if (view.lat !== undefined && view.lng !== undefined) {
          view.center = [view.lng, view.lat];
        }
        app.bus.send('map/animateToView', { ...view, relSpeed: 2 });
      }
    }
    if (R.has('ord', viewParams)) {
      app.bus.send('map/changeOrdinal', { ordinal: viewParams.ord });
    }
  };

  app.bus.on('deepLinking/setState', (payload: { id?: string } & DeepLinkViewState) => {
    if (payload.id === 'mapRenderer') {
      animateToState(payload);
    }
  });

  const showDefaultView = (): void => {
    const { defaultView } = state;
    if (!defaultView) {
      return;
    }
    app.bus.send('map/animateToView', {
      ...defaultView,
      center: [defaultView.lng, defaultView.lat],
      relSpeed: 2,
    });
  };

  app.bus.on('app/reset', () => showDefaultView());

  app.bus.on('map/setDefaultView', (view: Partial<ViewState>) => {
    state = {
      ...state,
      defaultView: { ...state.defaultView, ...view } as DefaultView,
    };
  });

  app.bus.on('map/showDefaultView', () => showDefaultView());

  app.bus.on('map/getViewBBox', () =>
    mapInitialized().then((map: MapInstance): BBox => {
      const mbp = map.getBounds();
      return [mbp.getWest(), mbp.getSouth(), mbp.getEast(), mbp.getNorth()];
    }),
  );

  app.bus.on('map/getMapCenter', () => {
    return mapInitialized().then(async (map: MapInstance): Promise<CenterPosition> => {
      if (!map.getCenter) {
        throw Error('*** Error in stateController:92 - map.getCenter not a function!');
      }

      const { lat, lng } = map.getCenter();
      const ordinal = await app.bus.get('map/getCurrentOrdinal');
      const mapviewBBox = await app.bus.get('map/getViewBBox');

      const { structure, floor } = getStructureAndFloorAtPoint(
        state.structures ?? [],
        lat,
        lng,
        ordinal ?? 0,
        mapviewBBox,
      ) as {
        structure: VenueStructure | null;
        floor: { id?: Id | null } | null;
      };
      return {
        lat,
        lng,
        ordinal,
        structureId: structure?.id,
        floorId: floor?.id,
      };
    });
  });

  app.bus.on('map/getCurrentView', () => mapInitialized().then((map: MapInstance) => getMapState(map)));

  app.bus.on('map/zoomIn', () =>
    mapInitialized().then((map: MapInstance) =>
      app.bus.send('map/animateToView', {
        zoom: map.getZoom() + 1,
        relSpeed: 3,
        mapAction: 'zoomIn',
      }),
    ),
  );

  app.bus.on('map/zoomOut', () =>
    mapInitialized().then((map: MapInstance) =>
      app.bus.send('map/animateToView', {
        zoom: map.getZoom() - 1,
        relSpeed: 3,
        mapAction: 'zoomOut',
      }),
    ),
  );

  app.bus.on('map/zoomTo', ({ value }: { value: number }) =>
    mapInitialized().then(() =>
      app.bus.send('map/animateToView', {
        zoom: value,
        relSpeed: 3,
        mapAction: 'zoomTo',
      }),
    ),
  );

  app.bus.on('map/zoomBy', ({ value }: { value: number }) =>
    mapInitialized().then((map: MapInstance) =>
      app.bus.send('map/animateToView', {
        zoom: map.getZoom() + value,
        relSpeed: 3,
        mapAction: value > 0 ? 'zoomIn' : 'zoomOut',
      }),
    ),
  );

  app.bus.on('map/setBearing', ({ bearing }: { bearing: number }) =>
    mapInitialized().then(() => app.bus.send('map/animateToView', { bearing, relSpeed: 2 })),
  );

  app.bus.on('map/resetBearing', () =>
    mapInitialized().then(() => app.bus.send('map/animateToView', { bearing: 0, relSpeed: 2 })),
  );

  app.bus.on('map/setPitch', ({ pitch }: { pitch: number }) =>
    mapInitialized().then(() => app.bus.send('map/animateToView', { pitch, relSpeed: 2 })),
  );

  app.bus.on('map/rotateRight', ({ delta = 1 }: { delta?: number } = {}) =>
    mapInitialized().then((map: MapInstance) =>
      app.bus.send('map/animateToView', {
        bearing: map.getBearing() - delta,
        relSpeed: 3,
      }),
    ),
  );

  app.bus.on('map/rotateLeft', ({ delta = 1 }: { delta?: number } = {}) =>
    mapInitialized().then((map: MapInstance) =>
      app.bus.send('map/animateToView', {
        bearing: map.getBearing() + delta,
        relSpeed: 3,
      }),
    ),
  );

  app.bus.on('map/getVisibleEntities', () => {
    return mapInitialized().then((map: MapInstance): Id[] => {
      const clientRect = map.getCanvas().getBoundingClientRect();
      const padding = map.getPadding();
      const bbox: [[number, number], [number, number]] = [
        [clientRect.x + (padding.left ?? 0), clientRect.y + (padding.top ?? 0)],
        [
          clientRect.x + clientRect.width - (padding.right ?? 0),
          clientRect.y + clientRect.height - (padding.bottom ?? 0),
        ],
      ];
      const allLayers = map
        .getStyle()
        .layers.filter((layer: { type?: string; id: string; source?: string }) => {
          return layer.type === 'symbol' && !layer.id.includes(' dot') && layer.source === 'llOrdinalSource';
        })
        .map((layer: { id: string }) => layer.id);
      const selectedLayers = allLayers.filter(layerId => layerId.includes('_selected'));
      const visibleSelectedRenderedFeatures = map.queryRenderedFeatures(bbox, {
        layers: selectedLayers,
      }) as MapFeature[];
      const visibleEntities: Id[] = [];
      if (visibleSelectedRenderedFeatures.length > 0) {
        const visibleSelectedEntityIds = visibleSelectedRenderedFeatures
          .filter(feature => feature.properties.aiLayer === 'poi')
          .map(feature => feature.properties.id)
          .filter((id): id is Id => id !== undefined && id !== null);
        visibleEntities.push(...visibleSelectedEntityIds);
      } else {
        const nothingSelectedLayers = allLayers.filter(
          layerId => !layerId.includes('_selected') && !layerId.includes('_notSelected'),
        );
        const visibleRenderedFeatures = map.queryRenderedFeatures(bbox, {
          layers: nothingSelectedLayers,
        }) as MapFeature[];
        const visibleEntityIds = visibleRenderedFeatures
          .filter(feature => feature.properties.aiLayer === 'poi')
          .map(feature => feature.properties.id)
          .filter((id): id is Id => id !== undefined && id !== null);
        visibleEntities.push(...visibleEntityIds);
      }
      return R.uniq(visibleEntities);
    });
  });

  app.bus.on('map/getCurrentMapRadius', () => {
    return mapInitialized().then(async (map: MapInstance) => {
      const venueCenter = await app.bus.get<undefined, { lat: number }>('venueData/getVenueCenter');
      return convertZoomToRadius(map.getZoom(), venueCenter, map, app.config.renderDiv);
    });
  });

  const t6 = (n: number): number => Math.round(n * 10 ** 6) / 10 ** 6;

  const notifyState = async ({ bearing, lng, lat, pitch, zoom }: ViewState | MoveEndPayload): Promise<void> => {
    const ordinal = await app.bus.get('map/getCurrentOrdinal');
    app.bus.send('deepLinking/notifyState', {
      id: 'mapRenderer',
      vp: {
        lat: t6(lat),
        lng: t6(lng),
        zoom: t6(zoom),
        bearing: t6(bearing),
        pitch: t6(pitch),
      },
      ord: ordinal,
    });
  };

  app.bus.on('map/ordinalChanged', async () => {
    const view = await app.bus.get<undefined, ViewState>('map/getCurrentView');
    notifyState(view);
  });

  app.bus.on('venueData/loadNewVenue', () => {
    state = {};
  });

  const runTest = async (initialState: Partial<StateControllerState>, testRoutine: () => Promise<void> | void) => {
    state = R.mergeRight(state, initialState);
    await testRoutine();
    return state;
  };

  return { runTest };
}
